/**
 * Card Program Thresholds & Defaults
 *
 * Centralized configuration for commercial card program constants.
 * These values serve as defaults when no card-program-level override exists.
 *
 * IMPORTANT: Large Ticket thresholds are TRANSACTION-LEVEL qualifiers, not
 * program tiers. A transaction qualifies when its dollar amount exceeds the
 * network threshold AND the vendor passes enhanced L2/L3 data.
 *
 * See: src/docs/specs/CARD_REBATE_STRUCTURE.md
 */

/**
 * Network-specific Large Ticket minimum transaction amounts.
 * These are the amounts above which a single transaction may qualify
 * for large ticket interchange rates (lower cost, different rebate calc).
 *
 * Source:
 * - Visa: Commercial Large Ticket program, transactions > $8,000
 * - Mastercard: Large Ticket program, transactions > $10,000
 */
export const LARGE_TICKET_THRESHOLDS = {
  /** Visa Commercial Large Ticket — transactions above this amount */
  visa: 8_000,
  /** Mastercard Commercial Payments Large Ticket — transactions above this amount */
  mastercard: 10_000,
  /**
   * Default threshold used when network is unknown or for general screening.
   * Conservative (lower) value to avoid missing potential qualifiers.
   */
  default: 7_000,
} as const;

/**
 * Default Large Ticket enrollment rate for opportunity analysis.
 * Represents the expected percentage of large-ticket-eligible vendors
 * that will actually be enrolled/activated.
 */
export const LARGE_TICKET_DEFAULT_ENROLLMENT_RATE = 27;

/**
 * Maximum annual spend threshold for large ticket opportunity screening.
 * Vendors above this spend level are typically better served by
 * standard volume-tier rates or custom VIP arrangements.
 */
export const LARGE_TICKET_MAX_ANNUAL_SPEND = 500_000;
